import type { EmailSnippet } from '../types';
import { toEditableHtml } from './emailImages';

/**
 * Helpers for the mail editor's "/" snippet menu (see docs/EMAIL_SNIPPETS.md).
 *
 * Snippets are rich text, so the menu needs a plain-text rendering to show and to filter on,
 * and insertion needs to decide between continuing the current paragraph and adding new ones.
 */

/** Mirrors the caps enforced on write in `bookmyband-api/app/models/user.py`. */
export const MAX_SNIPPETS = 100;
export const MAX_SNIPPET_TAG_LENGTH = 40;
export const MAX_SNIPPET_HTML_LENGTH = 5000;

/** Lowercase and strip accents, so "reponse" matches "réponse". */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/** The snippet's text without its markup, for the menu row and for text matching. */
export function snippetText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** First line of the snippet, which is what a multi-paragraph row shows. */
export function snippetLabel(snippet: EmailSnippet): string {
  return snippetText(snippet.html) || snippet.tag;
}

export interface SnippetMatch {
  snippet: EmailSnippet;
  /** Position in the user's list — the menu's tiebreak, so equally good matches keep their order. */
  index: number;
  text: string;
}

/**
 * Snippets matching `query`, best first: tag prefix, then tag substring, then text prefix, then
 * text substring. An empty query returns the whole list in the order the user arranged it.
 */
export function filterSnippets(snippets: EmailSnippet[], query: string): SnippetMatch[] {
  const entries = snippets.map((snippet, index) => ({
    snippet,
    index,
    text: snippetText(snippet.html),
  }));

  const q = normalize(query);
  if (!q) return entries;

  const scored: { entry: SnippetMatch; score: number }[] = [];
  for (const entry of entries) {
    const tag = normalize(entry.snippet.tag);
    const text = normalize(entry.text);
    let score: number | null = null;
    if (tag && tag.startsWith(q)) score = 0;
    else if (tag && tag.includes(q)) score = 1;
    else if (text.startsWith(q)) score = 2;
    else if (text.includes(q)) score = 3;
    if (score !== null) scored.push({ entry, score });
  }

  return scored
    .sort((a, b) => a.score - b.score || a.entry.index - b.entry.index)
    .map(({ entry }) => entry);
}

/**
 * The HTML to insert at the cursor. A single-paragraph snippet is unwrapped so it continues the
 * sentence being typed instead of splitting the paragraph in two; anything with more structure
 * (several paragraphs, a list) is inserted as-is and keeps its blocks.
 *
 * A snippet carrying an image — a signature block with a logo — is stored with a `cid:`
 * reference, which will not display in a browser, so it is resolved on the way in like any other
 * stored body (docs/EMAIL_IMAGES.md).
 */
export function snippetInsertHtml(html: string): string {
  const trimmed = toEditableHtml(html).trim();
  const single = /^<p(?:\s[^>]*)?>([\s\S]*)<\/p>$/i.exec(trimmed);
  if (single && !/<(?:p|ul|ol|li|h[1-6]|blockquote|pre|div)[\s>]/i.test(single[1])) {
    return single[1];
  }
  return trimmed;
}
